import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Empty, PageHeader } from "@/components/PageHeader";
import { OvernightTag } from "@/components/ShiftChip";
import { api, errMsg } from "@/lib/api";
import { fmtDay, fmtTime, isOvernight } from "@/lib/dates";

export const TYPE_LABEL = { timeoff: "Time off", swap: "Shift swap", claim: "Open shift claim" };
export const TYPE_STYLE = { timeoff: "bg-rose-600", swap: "bg-purple-600", claim: "bg-amber-500 text-slate-950" };
export const STATUS_STYLE = { pending: "bg-slate-200 text-slate-700", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-rose-100 text-rose-700", cancelled: "bg-slate-100 text-slate-400" };

export const ShiftLine = ({ shift }) => shift ? (
  <span className="font-mono text-sm">{fmtDay(shift.start)} · {fmtTime(shift.start)}–{fmtTime(shift.end)}{isOvernight(shift) && <OvernightTag />}</span>
) : <span className="text-sm text-slate-400">Shift removed</span>;

const RequestCard = ({ r, onDecide }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`request-card-${r.id}`}>
    <div className="flex flex-wrap items-center gap-2">
      <Badge className={`${TYPE_STYLE[r.type]} text-white`}>{TYPE_LABEL[r.type]}</Badge>
      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[r.status]}`} data-testid={`request-status-${r.id}`}>{r.status}</span>
      <span className="ml-auto text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString("en-GB")}</span>
    </div>
    <p className="mt-3 font-semibold">{r.staff_name}</p>
    <div className="mt-1 text-slate-600">
      {r.type === "timeoff" && <span className="font-mono text-sm">{fmtDay(r.start_date)} → {fmtDay(r.end_date)}</span>}
      {r.type !== "timeoff" && <ShiftLine shift={r.shift} />}
      {r.type === "swap" && <p className="mt-1 flex items-center gap-1 text-sm"><ArrowRight className="h-3.5 w-3.5" /> to {r.target_staff_name}</p>}
    </div>
    {r.reason && <p className="mt-2 text-sm italic text-slate-500">"{r.reason}"</p>}
    {r.conflicts > 0 && r.status === "pending" && (
      <p className="mt-3 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" data-testid={`request-conflict-${r.id}`}>
        <AlertTriangle className="h-4 w-4" /> {r.conflicts} scheduled shift(s) clash with this time off
      </p>
    )}
    {r.status === "pending" && (
      <div className="mt-4 flex gap-2">
        <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => onDecide(r.id, true)} data-testid={`request-approve-btn-${r.id}`}><Check className="mr-1 h-4 w-4" />Approve</Button>
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => onDecide(r.id, false)} data-testid={`request-reject-btn-${r.id}`}><X className="mr-1 h-4 w-4" />Reject</Button>
      </div>
    )}
  </div>
);

export default function Requests() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get("/biz/requests", { params: tab === "pending" ? { status: "pending" } : {} }).then((r) => setRows(r.data));
  }, [tab]);
  useEffect(() => {
    load();
  }, [load]);

  const decide = async (id, approve) => {
    try {
      await api.post(`/biz/requests/${id}/decide`, { approve });
      toast.success(approve ? "Approved" : "Rejected");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader title="Requests" subtitle="Time off, shift swaps and open-shift claims">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList><TabsTrigger value="pending" data-testid="requests-tab-pending">Pending</TabsTrigger><TabsTrigger value="all" data-testid="requests-tab-all">All</TabsTrigger></TabsList>
        </Tabs>
      </PageHeader>
      {rows.length === 0 ? <Empty testId="requests-empty">Nothing waiting for you.</Empty> : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{rows.map((r) => <RequestCard key={r.id} r={r} onDecide={decide} />)}</div>
      )}
    </div>
  );
}
